/**
 * 机器人控制台：status 展示、ctrl 命令下发、模型 zip 分块上传。
 * 视频区由 WhepPlayer（WebRTC 拉流并叠加检测框）承载。
 */
import { useEffect, useRef, useState } from "react";
import { useMqtt, useTopic } from "./mqtt";
import { WhepPlayer } from "./WhepPlayer";

const CHUNK_SIZE = 256 * 1024; // 与机器人 config.CHUNK_SIZE 一致
const MODEL_MAX_BYTES = 100 * 1024 * 1024;

interface RobotStatus {
  online?: boolean;
  model_id?: string;
  model_sha256?: string;
  conf?: number;
  classes?: string[] | null;
  infer_ms?: number;
  infer_fps?: number;
  ts?: number;
}

interface Ack {
  req_id: string;
  ok: boolean;
  conf?: number;
  classes?: string[] | null;
  error?: string;
  at: number;
}

interface ModelStatusMsg {
  upload_id: string;
  state: string;
  model_id?: string;
  error?: string;
  detail?: string;
}

interface UploadState {
  phase: "idle" | "sending" | "sent" | "error";
  sent: number;
  total: number;
  uploadId?: string;
  error?: string;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const STEP = 0x8000;
  for (let i = 0; i < bytes.length; i += STEP) {
    bin += String.fromCharCode(...bytes.subarray(i, i + STEP));
  }
  return btoa(bin);
}

function bufToHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, "0")).join("");
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

export function RobotPanel() {
  const { connState, publishMsg } = useMqtt();
  const connected = connState === "connected";

  const [robotId, setRobotId] = useState(() => localStorage.getItem("robot-id") ?? "dog-01");
  useEffect(() => {
    localStorage.setItem("robot-id", robotId);
  }, [robotId]);

  const t = (suffix: string) => `robot/${robotId}/${suffix}`;

  // ---- 状态 / 应答 / 模型状态 ----
  const [status, setStatus] = useState<RobotStatus | null>(null);
  const [acks, setAcks] = useState<Ack[]>([]);
  const [modelStatus, setModelStatus] = useState<ModelStatusMsg | null>(null);

  useTopic(t("status"), (_topic, payload) => {
    try {
      setStatus(JSON.parse(payload));
    } catch {
      // 非 JSON 忽略
    }
  });
  useTopic(t("ctrl_ack"), (_topic, payload) => {
    try {
      const a = JSON.parse(payload) as Omit<Ack, "at">;
      setAcks(prev => [{ ...a, at: Date.now() }, ...prev].slice(0, 6));
    } catch {
      // ignore
    }
  });
  useTopic(t("model/status"), (_topic, payload) => {
    try {
      setModelStatus(JSON.parse(payload));
    } catch {
      // ignore
    }
  });

  // ---- ctrl 命令 ----
  const [confValue, setConfValue] = useState("0.35");
  const [labelsInput, setLabelsInput] = useState("");

  const sendCtrl = (cmd: string, extra: Record<string, unknown> = {}) => {
    const req = {
      cmd,
      ...extra,
      req_id: `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    };
    publishMsg(t("ctrl"), JSON.stringify(req), 1);
  };

  const sendSetConf = () => {
    const v = Number(confValue);
    if (!Number.isFinite(v) || v < 0 || v > 1) return;
    sendCtrl("set_conf", { value: v });
  };

  const sendSetClasses = () => {
    const labels = labelsInput.split(/[,，\s]+/).map(s => s.trim()).filter(Boolean);
    // 留空 = 不带 labels 字段 = 全部启用（与机器人端约定一致）
    sendCtrl("set_classes", labels.length > 0 ? { labels } : {});
  };

  // ---- 模型上传 ----
  const [file, setFile] = useState<File | null>(null);
  const [upload, setUpload] = useState<UploadState>({ phase: "idle", sent: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startUpload = async () => {
    if (!file || !connected || upload.phase === "sending") return;
    if (file.size > MODEL_MAX_BYTES) {
      setUpload({ phase: "error", sent: 0, total: 0, error: `文件超过机器人上限（${MODEL_MAX_BYTES / 1024 / 1024}MB）` });
      return;
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const sha256 = bufToHex(await crypto.subtle.digest("SHA-256", bytes));
    const total = Math.max(1, Math.ceil(bytes.length / CHUNK_SIZE));
    const uploadId = `web-${Date.now()}`;
    setUpload({ phase: "sending", sent: 0, total, uploadId });

    for (let i = 0; i < total; i++) {
      const chunk = bytesToBase64(bytes.subarray(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      const ok = publishMsg(
        t("model/chunk"),
        JSON.stringify({ upload_id: uploadId, chunk_idx: i, total_chunks: total, sha256, data: chunk }),
        1,
      );
      if (!ok) {
        setUpload(s => ({ ...s, phase: "error", error: "连接已断开，上传中止" }));
        return;
      }
      setUpload(s => ({ ...s, sent: i + 1 }));
      await sleep(15); // 轻微限速，避免瞬间打满 broker
    }
    setUpload(s => ({ ...s, phase: "sent" }));
  };

  // 与本次 upload_id 匹配的机器人处理结果
  const uploadResult =
    upload.uploadId && modelStatus?.upload_id === upload.uploadId ? modelStatus : null;

  const onlineBadge = () => {
    if (!status) return <span className="badge badge-gray">未收到 status（保留消息）</span>;
    return status.online ? (
      <span className="badge badge-green">在线</span>
    ) : (
      <span className="badge badge-red">离线（LWT）</span>
    );
  };

  return (
    <div className="panel">
      {/* 顶部：robot id + 状态卡 */}
      <section className="card">
        <div className="card-head">
          <h3>运行状态</h3>
          {onlineBadge()}
          <span className="grow" />
          <label className="row-inline">
            robot_id
            <input className="input mono" value={robotId} onChange={e => setRobotId(e.target.value.trim())} style={{ width: 110 }} />
          </label>
        </div>
        <div className="status-grid">
          <div className="status-item">
            <span className="k">model_id</span>
            <span className="v mono">{status?.model_id ?? "—"}</span>
          </div>
          <div className="status-item">
            <span className="k">sha256</span>
            <span className="v mono" title={status?.model_sha256}>
              {status?.model_sha256 ? status.model_sha256.slice(0, 12) + "…" : "—"}
            </span>
          </div>
          <div className="status-item">
            <span className="k">conf</span>
            <span className="v mono">{status?.conf ?? "—"}</span>
          </div>
          <div className="status-item">
            <span className="k">infer</span>
            <span className="v mono">
              {status?.infer_ms != null ? `${status.infer_ms}ms / ${status.infer_fps ?? "?"}fps` : "—"}
            </span>
          </div>
          <div className="status-item wide">
            <span className="k">classes</span>
            <span className="v">
              {status == null
                ? "—"
                : status.classes == null
                  ? "全部启用"
                  : status.classes.length
                    ? status.classes.map(c => <span key={c} className="chip">{c}</span>)
                    : "（空）"}
            </span>
          </div>
        </div>
      </section>

      {/* 视频区：WebRTC 拉流，检测框直接叠加在画面上 */}
      <WhepPlayer robotId={robotId} />

      <div className="grid-2">
        {/* ctrl 命令 */}
        <section className="card">
          <div className="card-head">
            <h3>控制命令</h3>
            <span className="tag mono">{t("ctrl")}</span>
          </div>
          <div className="row">
            <span className="cmd-name mono">set_conf</span>
            <input
              className="input mono"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={confValue}
              onChange={e => setConfValue(e.target.value)}
              style={{ width: 100 }}
            />
            <button className="btn btn-primary" disabled={!connected} onClick={sendSetConf}>
              发送
            </button>
          </div>
          <div className="row">
            <span className="cmd-name mono">set_classes</span>
            <input
              className="input mono grow"
              value={labelsInput}
              placeholder="逗号分隔的 label，留空 = 全部启用"
              onChange={e => setLabelsInput(e.target.value)}
            />
            <button className="btn btn-primary" disabled={!connected} onClick={sendSetClasses}>
              发送
            </button>
          </div>
          <h4>ctrl_ack</h4>
          {acks.length === 0 && <p className="hint">暂无应答</p>}
          <ul className="ack-list">
            {acks.map(a => (
              <li key={a.req_id} className={a.ok ? "ok" : "fail"}>
                <span className="mono">{new Date(a.at).toLocaleTimeString("zh-CN", { hour12: false })}</span>
                <span>{a.ok ? "✓" : "✗"} {a.error ?? ""}</span>
                <span className="mono">conf={a.conf ?? "?"}</span>
                <span className="mono">{a.classes == null ? "全部类别" : `[${a.classes.join(", ")}]`}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* 模型上传 */}
        <section className="card">
          <div className="card-head">
            <h3>模型热更新</h3>
            <span className="tag mono">{t("model/chunk")}</span>
            <span className="tag">{CHUNK_SIZE / 1024}KB 分块</span>
          </div>
          <div className="row">
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={e => {
                setFile(e.target.files?.[0] ?? null);
                setUpload({ phase: "idle", sent: 0, total: 0 });
                setModelStatus(null);
              }}
            />
          </div>
          {file && (
            <p className="hint">
              {file.name} · {(file.size / 1024 / 1024).toFixed(2)}MB ·{" "}
              {Math.max(1, Math.ceil(file.size / CHUNK_SIZE))} 块
            </p>
          )}
          <div className="row">
            <button
              className="btn btn-primary"
              disabled={!connected || !file || upload.phase === "sending"}
              onClick={startUpload}
            >
              {upload.phase === "sending" ? `发送中 ${upload.sent}/${upload.total}` : "开始上传"}
            </button>
            {upload.phase === "sent" && <span className="hint">已全部发出，等待机器人处理结果…</span>}
          </div>
          {upload.phase === "sending" && (
            <div className="progress">
              <div className="progress-bar" style={{ width: `${(upload.sent / upload.total) * 100}%` }} />
            </div>
          )}
          {upload.phase === "error" && (
            <p className="msg-error">{upload.error}</p>
          )}
          {uploadResult && (
            <p className={uploadResult.state === "complete" ? "msg-ok" : "msg-error"}>
              {uploadResult.state === "complete"
                ? `✓ 模型已热切换 -> ${uploadResult.model_id}`
                : `✗ ${uploadResult.error}${uploadResult.detail ? `：${uploadResult.detail}` : ""}`}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
